import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CollectionsService, HomepageSettingsService } from '../firebase';
import { Collection } from '../types/collection';

const CollectionsContainer = styled.section`
  padding: 100px 0;
  background: #fff;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;

  h2 {
    font-size: 2.5rem;
    font-weight: 300;
    color: #2c2c2c;
    margin-bottom: 20px;
  }

  p {
    font-size: 1.1rem;
    color: #666;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const CollectionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
`;

const CollectionCard = styled.div`
  position: relative;
  height: 400px;
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-10px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.1));
    z-index: 1;
  }
`;

const CollectionImage = styled.div<{ $bgImage: string }>`
  width: 100%;
  height: 100%;
  background: url(${props => props.$bgImage}) center/cover;
`;

const CollectionContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 30px;
  color: #fff;
  z-index: 2;

  h3 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 10px;
  }

  p {
    font-size: 1rem;
    opacity: 0.9;
    margin-bottom: 20px;
  }
`;

const CollectionButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 10px 20px;
  border-radius: 25px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateX(5px);
  }
`;

const LoadingContainer = styled.div`
  padding: 60px 20px;
  text-align: center;
  color: #666;
`;

const EmptyContainer = styled.div`
  padding: 60px 20px;
  text-align: center;
  color: #999;
`;

const CollectionsSection: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [displayedCollections, setDisplayedCollections] = useState<Collection[]>([]);

  useEffect(() => {
    loadCollections();
  }, []);

  const loadCollections = async () => {
    setLoading(true);
    try {
      // Önce homepage settings'den hangi koleksiyonların gösterileceğini al
      const settingsResult = await HomepageSettingsService.getHomepageSettings();
      
      if (settingsResult.success && settingsResult.data?.collections && settingsResult.data.collections.length > 0) {
        // Ana sayfa ayarlarında belirtilen koleksiyonları yükle
        const collectionIds = settingsResult.data.collections;
        const allCollectionsResult = await CollectionsService.getCollections();
        
        if (allCollectionsResult.success && allCollectionsResult.data) {
          // Sadece aktif ve sıralanmış koleksiyonları filtrele
          const activeCollections = allCollectionsResult.data
            .filter(collection => 
              collection.isActive && 
              collectionIds.includes(collection.id || '')
            )
            .sort((a, b) => {
              // Ana sayfa ayarlarındaki sıraya göre sırala
              const aIndex = collectionIds.indexOf(a.id || '');
              const bIndex = collectionIds.indexOf(b.id || '');
              return aIndex - bIndex;
            });
          
          setDisplayedCollections(activeCollections);
        }
      } else {
        // Ana sayfa ayarları yoksa tüm aktif koleksiyonları göster
        const result = await CollectionsService.getActiveCollections();
        if (result.success && result.data) {
          setDisplayedCollections(result.data);
        }
      }
    } catch (error) {
      console.error('Error loading collections:', error);
      // Hata durumunda boş liste göster
      setDisplayedCollections([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <CollectionsContainer>
        <Container>
          <LoadingContainer>Yükleniyor...</LoadingContainer>
        </Container>
      </CollectionsContainer>
    );
  }

  if (displayedCollections.length === 0) {
    return (
      <CollectionsContainer>
        <Container>
          <SectionHeader>
            <h2>Koleksiyonlarımız</h2>
            <p>
              Her koleksiyonumuz, farklı tarz ve zevklere hitap eden özel tasarımlarla hazırlanmıştır. 
              Hayalinizdeki gelinliği bulun.
            </p>
          </SectionHeader>
          <EmptyContainer>
            Henüz koleksiyon eklenmedi
          </EmptyContainer>
        </Container>
      </CollectionsContainer>
    );
  }

  return (
    <CollectionsContainer>
      <Container>
        <SectionHeader>
          <h2>Koleksiyonlarımız</h2>
          <p>
            Her koleksiyonumuz, farklı tarz ve zevklere hitap eden özel tasarımlarla hazırlanmıştır. 
            Hayalinizdeki gelinliği bulun.
          </p>
        </SectionHeader>

        <CollectionsGrid>
          {displayedCollections.map((collection) => (
            <CollectionCard key={collection.id} onClick={() => navigate('/gelinlikler')}>
              <CollectionImage $bgImage={collection.coverImage} />
              <CollectionContent>
                <h3>{collection.title}</h3>
                <p>{collection.description}</p>
                <CollectionButton>
                  Keşfet
                  <ArrowRight size={16} />
                </CollectionButton>
              </CollectionContent>
            </CollectionCard>
          ))}
        </CollectionsGrid>
      </Container>
    </CollectionsContainer>
  );
};

export default CollectionsSection;
